/**
 * Absence management page with table layout and approval queue
 * Displays all employee absences with filtering, search, column management, and CSV export
 * Includes an approval queue tab for pending absence requests
 */

import { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { useAuth } from "@clerk/clerk-react";
import { format, startOfMonth } from "date-fns";
import { Plus, Loader2, Check, X, Clock, CalendarOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { EmptyState } from "@/components/table/EmptyState";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { HelpTip } from "@/components/ui/help-tip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CreateAbsenceDialog } from "@/components/absences/CreateAbsenceDialog";
import { EditAbsenceDialog } from "@/components/absences/EditAbsenceDialog";
import { AbsenceTable } from "@/components/absences/AbsenceTable";
import { CancelReportDialog } from "@/components/absences/CancelReportDialog";
import { AbsenceCertificatesDialog } from "@/components/absences/AbsenceCertificatesDialog";
import { AbsenceTypeBadge, getAbsenceTypeLabel } from "@/components/absences/AbsenceTypeBadge";
import { AbsenceApprovalContext } from "@/components/absences/AbsenceApprovalContext";
import {
  useEmployeeAbsences,
  usePendingAbsences,
  useCountPendingAbsences,
  useApproveAbsence,
  useRejectAbsence,
  useAcknowledgeAbsence,
  useCancelAbsenceReport,
  useMarkEauRetrieved,
} from "@/hooks/useEmployeeAbsences";
import { useAbsenceSettings } from "@/hooks/useAbsenceSettings";
import { useAccess } from "@/hooks/useAccess";
import { useUrlTab } from "@/hooks/useUrlTab";
import { formatDateRange } from "@/lib/format";
import {
  ApprovalStatus,
  CertificateMode,
  CertificateStatus,
} from "@/types/employeeAbsences";
import type {
  CertificateModeValue,
  EmployeeAbsence,
  EmployeeAbsenceListParams,
} from "@/types/employeeAbsences";

/** Report filter presets of the absences tab (#1767); "" shows every absence. */
type ReportFilter = "" | "reports" | "unacknowledged" | "certificateMissing" | "eauPending";

const REPORT_FILTER_PARAMS: Record<ReportFilter, EmployeeAbsenceListParams> = {
  "": {},
  reports: { isSelfReport: true },
  // Only APPROVED reports can be acknowledged; a cancelled report must not stay in the queue.
  unacknowledged: {
    isSelfReport: true,
    acknowledged: false,
    approvalStatus: ApprovalStatus.APPROVED,
  },
  certificateMissing: { certificateStatus: CertificateStatus.DUE },
  // EAU mode: an APPROVED report whose eAU is not retrieved yet reads NOT_REQUIRED.
  eauPending: {
    isSelfReport: true,
    approvalStatus: ApprovalStatus.APPROVED,
    certificateStatus: CertificateStatus.NOT_REQUIRED,
  },
};

/**
 * Work-queue filters list every open item, however old: a month window would
 * hide a March report that is still unacknowledged or missing its certificate in April.
 */
const OPEN_QUEUE_FILTERS: ReadonlySet<ReportFilter> = new Set<ReportFilter>([
  "unacknowledged",
  "certificateMissing",
  "eauPending",
]);

/**
 * The inner tabs; the dashboard deep-links the queue as `?filter=approval&forMe=1`,
 * the "For me" scope its count uses.
 */
const INNER_TABS = ["absences", "approval"] as const;
type InnerTab = (typeof INNER_TABS)[number];

function isReportFilter(value: string): value is ReportFilter {
  return Object.prototype.hasOwnProperty.call(REPORT_FILTER_PARAMS, value);
}

/**
 * AbsenceManagementPage provides a full CRUD interface for employee absences.
 * Features filtering by employee, absence type, and date range via the table's built-in controls.
 * Includes an "Approval Queue" tab for managing pending absence requests.
 */
export function AbsenceManagementPage() {
  const { i18n } = useLingui();
  const { orgId } = useAuth();
  const { isAdmin, hasModule } = useAccess();

  // Write actions on reports (cancel, eAU retrieved, edit dates) and the
  // certificate viewer are for admins and `employees` holders only; a
  // `shifts-absences`-only user may acknowledge.
  const canWrite = isAdmin || hasModule("employees");
  const canReadCertificates = canWrite;
  const canReadAbsenceSettings = canWrite || hasModule("shifts-absences");

  const { data: absenceSettings } = useAbsenceSettings(orgId ?? "", {
    enabled: canReadAbsenceSettings,
  });
  // The generated settings DTO types the mode as a nominal enum with the same
  // two values; map it onto the plain union the table takes.
  const certificateMode: CertificateModeValue | undefined = absenceSettings
    ? String(absenceSettings.certificateMode) === CertificateMode.EAU
      ? CertificateMode.EAU
      : CertificateMode.UPLOAD
    : undefined;
  const isEauMode = certificateMode === CertificateMode.EAU;

  // Default date range - fetch from start of current month
  const defaultFromDate = useMemo(() => {
    return format(startOfMonth(new Date()), "yyyy-MM-dd");
  }, []);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] =
    useState<EmployeeAbsence | null>(null);

  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectAbsenceId, setRejectAbsenceId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Report row-action dialogs; the selected row is cleared by the handler that closes each.
  const [reportToCancel, setReportToCancel] = useState<EmployeeAbsence | null>(null);
  const [certificatesAbsenceId, setCertificatesAbsenceId] = useState<string | null>(null);

  // Tab state, in the URL so the dashboard link and a reload land on the queue
  const [activeTab, setActiveTab] = useUrlTab<InnerTab>("filter", INNER_TABS, "absences");

  // The pending request whose approve confirm is open (K4: shows its context)
  const [absenceToApprove, setAbsenceToApprove] = useState<EmployeeAbsence | null>(null);

  // Report filter (absences tab) and "For me" (approval tab), both off unless
  // the link asks for "For me". "For me" lives in the URL (replaced, not
  // pushed), so a reload or Back keeps what the user last chose.
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportFilter, setReportFilter] = useState<ReportFilter>("");
  const forMe = searchParams.get("forMe") === "1";
  const setForMe = useCallback(
    (on: boolean) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (on) next.set("forMe", "1");
          else next.delete("forMe");
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  // "eAU pending" exists only in EAU mode; a mode change drops it.
  const activeReportFilter: ReportFilter =
    reportFilter === "eauPending" && !isEauMode ? "" : reportFilter;

  // Query params - fetch a larger dataset and let the table handle filtering
  const queryParams = useMemo<EmployeeAbsenceListParams>(
    () => ({
      ...(OPEN_QUEUE_FILTERS.has(activeReportFilter) ? {} : { fromDate: defaultFromDate }),
      limit: 500,
      ...REPORT_FILTER_PARAMS[activeReportFilter],
    }),
    [defaultFromDate, activeReportFilter]
  );

  // Fetch absences
  const {
    data: absenceData,
    isLoading,
    error,
    refetch: refetchAbsences,
  } = useEmployeeAbsences(queryParams);

  // Fetch pending absences for approval queue
  const { data: pendingData, isLoading: pendingLoading } = usePendingAbsences({
    limit: 100,
    forMe,
  });

  // Fetch pending count for badge (same "For me" scope as the list)
  const { data: pendingCountData } = useCountPendingAbsences({ forMe });
  const pendingCount = pendingCountData?.count ?? 0;

  // Approval mutations
  const approveAbsence = useApproveAbsence();
  const rejectAbsenceMutation = useRejectAbsence();

  // Report actions (#1767, D27)
  const acknowledgeAbsence = useAcknowledgeAbsence();
  const cancelAbsenceReport = useCancelAbsenceReport();
  const markEauRetrieved = useMarkEauRetrieved();
  const { mutate: acknowledgeMutate } = acknowledgeAbsence;
  const { mutate: markEauRetrievedMutate } = markEauRetrieved;

  // Handlers
  const handleEditAbsence = useCallback((absence: EmployeeAbsence) => {
    setSelectedAbsence(absence);
    setEditDialogOpen(true);
  }, []);

  const handleAcknowledge = useCallback(
    (absence: EmployeeAbsence) => {
      if (absence.id) acknowledgeMutate(absence.id);
    },
    [acknowledgeMutate]
  );

  const handleEauRetrieved = useCallback(
    (absence: EmployeeAbsence) => {
      if (absence.id) markEauRetrievedMutate(absence.id);
    },
    [markEauRetrievedMutate]
  );

  const handleCancelReportClick = useCallback((absence: EmployeeAbsence) => {
    setReportToCancel(absence);
  }, []);

  const handleViewCertificates = useCallback((absence: EmployeeAbsence) => {
    setCertificatesAbsenceId(absence.id ?? null);
  }, []);

  const handleCancelReportOpenChange = (open: boolean) => {
    if (!open) setReportToCancel(null);
  };

  const handleCancelReportConfirm = async (reason: string) => {
    const id = reportToCancel?.id;
    if (!id) return;
    try {
      await cancelAbsenceReport.mutateAsync({ id, reason });
      // Close and clear the row together, only after the cancel succeeded.
      setReportToCancel(null);
    } catch {
      // The hook's onError shows the toast; the dialog stays open.
    }
  };

  const handleCertificatesOpenChange = (open: boolean) => {
    if (!open) setCertificatesAbsenceId(null);
  };

  // Resolves on success so the confirm closes; rejects (the hook toasts) so it stays open.
  const handleApproveConfirm = async () => {
    const id = absenceToApprove?.id;
    if (!id) return;
    await approveAbsence.mutateAsync(id);
    setAbsenceToApprove(null);
  };

  const handleRejectClick = (absenceId: string) => {
    setRejectAbsenceId(absenceId);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = () => {
    if (rejectAbsenceId && rejectReason.trim()) {
      rejectAbsenceMutation.mutate(
        { absenceId: rejectAbsenceId, reason: rejectReason.trim() },
        {
          onSuccess: () => {
            setRejectDialogOpen(false);
            setRejectAbsenceId(null);
            setRejectReason("");
          },
        }
      );
    }
  };

  if (error) {
    return (
      <QueryErrorState
        error={error}
        onRetry={() => void refetchAbsences()}
        title={i18n._({
          id: "common.error.fetch",
          message: "Failed to load data",
        })}
      />
    );
  }

  const absences = absenceData?.absences ?? [];
  const pendingAbsences = pendingData?.absences ?? [];

  return (
    <>
      <div className="space-y-6">
        {/* Action buttons row */}
        <div className="flex items-center justify-end">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {i18n._({ id: "common.add", message: "Add" })}
          </Button>
        </div>

        {/* Tabs: Absences + Approval Queue */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as InnerTab)}>
          <TabsList>
            <TabsTrigger value="absences">
              {i18n._({ id: "absences.tab.all", message: "All Absences" })}
            </TabsTrigger>
            <TabsTrigger value="approval" className="flex items-center gap-2">
              {i18n._({
                id: "absences.approval.title",
                message: "Approval Queue",
              })}
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 min-w-[1.25rem] px-1 text-xs">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* All Absences Tab */}
          <TabsContent value="absences" className="space-y-4">
            {/* Report filters (#1767). Each one carries its meaning as a tooltip (#2038 UX pass). */}
            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                className="flex-wrap justify-start"
                value={activeReportFilter}
                onValueChange={(value) => setReportFilter(isReportFilter(value) ? value : "")}
              >
                <ToggleGroupItem
                  value="reports"
                  title={i18n._({
                    id: "absences.filter.reportsTip",
                    message: "Absences the employees reported themselves in the app, for example a sick day.",
                  })}
                >
                  {i18n._({ id: "absences.filter.reports", message: "Reports" })}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="unacknowledged"
                  title={i18n._({
                    id: "absences.filter.unacknowledgedTip",
                    message: "Reports nobody in the office has marked as seen yet.",
                  })}
                >
                  {i18n._({ id: "absences.filter.unacknowledged", message: "Unacknowledged" })}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="certificateMissing"
                  title={i18n._({
                    id: "absences.filter.certificateMissingTip",
                    message: "Sick reports that need a doctor's certificate which has not arrived yet.",
                  })}
                >
                  {i18n._({
                    id: "absences.filter.certificateMissing",
                    message: "Certificate missing",
                  })}
                </ToggleGroupItem>
                {isEauMode && (
                  <ToggleGroupItem
                    value="eauPending"
                    title={i18n._({
                      id: "absences.filter.eauPendingTip",
                      message: "Sick reports whose electronic certificate (eAU) has not been retrieved from the health insurer yet.",
                    })}
                  >
                    {i18n._({ id: "absences.filter.eauPending", message: "eAU pending" })}
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
              <HelpTip
                data-testid="absence-filters-help"
                label={i18n._({ id: "absences.filter.helpLabel", message: "What these filters show" })}
                content={i18n._({
                  id: "absences.filter.help",
                  message:
                    "Without a filter the list shows every absence from the start of this month. The filters show the open work: reports from the app that nobody has acknowledged, and sick reports still missing a certificate. An approved absence cancels the employee's planned shifts on those days.",
                })}
              />
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
                <span className="ml-2 text-foreground">
                  {i18n._({
                    id: "common.loading.data",
                    message: "Loading data...",
                  })}
                </span>
              </div>
            ) : absences.length === 0 ? (
              activeReportFilter ? (
                <EmptyState
                  className="rounded-md border"
                  icon={<CalendarOff className="h-10 w-10" />}
                  title={i18n._({
                    id: "absences.empty.filteredTitle",
                    message: "No absences match this filter",
                  })}
                  action={
                    <Button size="sm" variant="outline" onClick={() => setReportFilter("")}>
                      {i18n._({ id: "absences.empty.showAll", message: "Show all absences" })}
                    </Button>
                  }
                />
              ) : (
                // The list starts at the first of the month, so "none yet" would be untrue.
                <EmptyState
                  className="rounded-md border"
                  icon={<CalendarOff className="h-10 w-10" />}
                  title={i18n._({
                    id: "absences.empty.title",
                    message: "No absences this month or later",
                  })}
                  description={i18n._({
                    id: "absences.empty.description",
                    message: "Absences you add and absences staff report appear here.",
                  })}
                  action={
                    <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      {i18n._({ id: "absences.empty.add", message: "Add absence" })}
                    </Button>
                  }
                />
              )
            ) : (
              <AbsenceTable
                data={absences}
                onEditAbsence={handleEditAbsence}
                onAcknowledge={handleAcknowledge}
                onCancelReport={handleCancelReportClick}
                onEauRetrieved={handleEauRetrieved}
                onEditDates={handleEditAbsence}
                onViewCertificates={canReadCertificates ? handleViewCertificates : undefined}
                certificateMode={certificateMode}
                canWrite={canWrite}
                canReadCertificates={canReadCertificates}
              />
            )}
          </TabsContent>

          {/* Approval Queue Tab */}
          <TabsContent value="approval" className="space-y-4">
            {/* "For me" (#1767): only the items routed to the caller, list and badge alike */}
            <div className="flex justify-start">
              <Toggle
                variant="outline"
                size="sm"
                pressed={forMe}
                onPressedChange={setForMe}
              >
                {i18n._({ id: "absences.filter.forMe", message: "For me" })}
              </Toggle>
            </div>

            {pendingLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
                <span className="ml-2 text-foreground">
                  {i18n._({
                    id: "common.loading.data",
                    message: "Loading data...",
                  })}
                </span>
              </div>
            ) : pendingAbsences.length === 0 ? (
              <EmptyState
                className="rounded-md border"
                icon={<Clock className="h-10 w-10" />}
                title={i18n._({
                  id: "absences.approval.empty",
                  message: "No pending requests",
                })}
                action={
                  forMe ? (
                    <Button size="sm" variant="outline" onClick={() => setForMe(false)}>
                      {i18n._({ id: "absences.approval.showAll", message: "Show all requests" })}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <div className="space-y-3">
                {pendingAbsences.map((absence) => (
                  <div
                    key={absence.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {absence.employee?.firstname}{" "}
                          {absence.employee?.lastname}
                        </span>
                        <AbsenceTypeBadge type={absence.absence_type ?? ""} />
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatDateRange(absence.from_date, absence.to_date, i18n.locale)}
                        {absence.notes && ` | ${absence.notes}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-success-on-surface hover:bg-success-muted hover:text-success-on-surface"
                        onClick={() => setAbsenceToApprove(absence)}
                        disabled={approveAbsence.isPending}
                      >
                        <Check className="h-4 w-4 mr-1" aria-hidden="true" />
                        {i18n._({
                          id: "absences.approval.approve",
                          message: "Approve",
                        })}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive-on-surface hover:bg-destructive-muted hover:text-destructive-on-surface"
                        /* An absence row from the API always carries an id; the generated model marks it optional. */
                        onClick={() => handleRejectClick(absence.id!)}
                        disabled={rejectAbsenceMutation.isPending}
                      >
                        <X className="h-4 w-4 mr-1" aria-hidden="true" />
                        {i18n._({
                          id: "absences.approval.reject",
                          message: "Reject",
                        })}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Dialog */}
      <CreateAbsenceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Edit Dialog */}
      {selectedAbsence && (
        <EditAbsenceDialog
          absence={selectedAbsence}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setSelectedAbsence(null);
          }}
          onSuccess={() => {
            setEditDialogOpen(false);
            setSelectedAbsence(null);
          }}
        />
      )}

      {/* Cancel Report Dialog (#1767) */}
      <CancelReportDialog
        open={reportToCancel !== null}
        absence={reportToCancel}
        isPending={cancelAbsenceReport.isPending}
        onConfirm={handleCancelReportConfirm}
        onOpenChange={handleCancelReportOpenChange}
      />

      {/* Certificate viewer (#1767): admins and employees holders only */}
      {canReadCertificates && (
        <AbsenceCertificatesDialog
          open={certificatesAbsenceId !== null}
          absenceId={certificatesAbsenceId}
          onOpenChange={handleCertificatesOpenChange}
        />
      )}

      {/* Approve confirm with the decision context (K4): a warning, never a block */}
      <ConfirmDialog
        open={absenceToApprove !== null}
        onOpenChange={(open) => {
          if (!open) setAbsenceToApprove(null);
        }}
        title={i18n._({
          id: "absences.approval.confirmTitle",
          message: "Approve this absence?",
        })}
        description={
          absenceToApprove
            ? i18n._({
                id: "absences.approval.confirmDescription",
                message: "{name}, {type}, {dates}",
                values: {
                  name: [absenceToApprove.employee?.firstname, absenceToApprove.employee?.lastname]
                    .filter(Boolean)
                    .join(" "),
                  type: getAbsenceTypeLabel(absenceToApprove.absence_type ?? "", i18n),
                  dates: formatDateRange(absenceToApprove.from_date, absenceToApprove.to_date, i18n.locale),
                },
              })
            : undefined
        }
        confirmLabel={i18n._({
          id: "absences.approval.approve",
          message: "Approve",
        })}
        onConfirm={handleApproveConfirm}
        isPending={approveAbsence.isPending}
      >
        {absenceToApprove && <AbsenceApprovalContext absence={absenceToApprove} />}
        {/* What approving does to the plan (#2038 UX pass) */}
        <p className="mt-3 text-xs text-muted-foreground" data-testid="approve-shift-note">
          {i18n._({
            id: "absences.approval.shiftNote",
            message:
              "Planned shifts of the employee on these days are cancelled and the employee is notified. The shift plan updates itself.",
          })}
        </p>
      </ConfirmDialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {i18n._({
                id: "absences.approval.rejectTitle",
                message: "Reject Absence Request",
              })}
            </DialogTitle>
            <DialogDescription className="sr-only">
              <Trans id="common.dialog.formDescription">Fill in the fields, then save.</Trans>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                {i18n._({
                  id: "absences.approval.reason",
                  message: "Rejection Reason",
                })}
              </Label>
              <Textarea
                id="rejection-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={10000}
                placeholder={i18n._({
                  id: "absences.approval.reasonPlaceholder",
                  message: "Please provide a reason for rejection...",
                })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
            >
              {i18n._({ id: "common.cancel", message: "Cancel" })}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectSubmit}
              disabled={
                !rejectReason.trim() || rejectAbsenceMutation.isPending
              }
            >
              {rejectAbsenceMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {i18n._({
                id: "absences.approval.reject",
                message: "Reject",
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AbsenceManagementPage;
